import type { Dispatch, RefObject, SetStateAction, SyntheticEvent } from 'react'

import { useCallback, useEffect, useRef } from 'react'
import { debounce } from 'utils/helpers'
import { ScrollBehavior } from '../RowContainer.h'

const getCurrentGap = (ref: RefObject<HTMLDivElement | null>): number => {
  if (!ref.current) return 0

  const computedStyle = window.getComputedStyle(ref.current)
  const gapValue = computedStyle.gap || computedStyle.getPropertyValue('gap')
  return parseInt(gapValue, 10) || 0
}

interface GetRowInfoData {
  cardWidth: number
  gap: number
}
const getRowInfo = (ref: RefObject<HTMLDivElement | null>): GetRowInfoData => {
  const firstChild = ref.current?.children[0]
  const cardWidth = firstChild?.getBoundingClientRect()?.width || 0
  const gap = getCurrentGap(ref)

  return {
    cardWidth,
    gap,
  }
}

interface IRowScrollHandlersProps {
  ref: RefObject<HTMLDivElement | null>
  visibleCards: number
  setRowScrollPosition: Dispatch<SetStateAction<number>>
}
export const useSimpleRowScrollHandlers = ({
  ref,
  visibleCards,
  setRowScrollPosition,
}: IRowScrollHandlersProps) => {
  const DELAY_SET_SCROLL_POSITION = 150
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const scrollToItem = ({
    behavior = ScrollBehavior.smooth,
    forward = false,
  }: {
    behavior?: ScrollBehavior
    forward?: boolean
  }): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    // add a bit delay to scroll to the item,
    // ensuring that the item is rendered before scrolling
    timeoutRef.current = setTimeout(() => {
      const { cardWidth, gap } = getRowInfo(ref)
      let scrollPosition = Math.floor(visibleCards) * (cardWidth + gap * 2)

      const currentScrollPosition = ref.current?.scrollLeft || 0

      const scrollBackPosition = currentScrollPosition - scrollPosition
      const scrollForwardPosition = currentScrollPosition + scrollPosition
      const scrollTo = forward
        ? scrollForwardPosition
        : scrollBackPosition > 0
          ? scrollBackPosition
          : 0

      ref.current?.scrollTo({
        left: scrollTo,
        behavior,
      })
    }, 10)
  }

  const focusOnItem = ({
    behavior = ScrollBehavior.smooth,
    scrollItemIndex,
  }: {
    behavior?: ScrollBehavior
    scrollItemIndex: number
  }): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      const { cardWidth, gap } = getRowInfo(ref)

      // Calculate position to center the selected item
      const containerWidth = ref.current?.clientWidth || 0
      const itemPosition = Math.floor(scrollItemIndex) * (cardWidth + gap)
      const centerOffset = (containerWidth - cardWidth) / 2
      const scrollTo = Math.max(0, itemPosition - centerOffset)

      ref.current?.scrollTo({
        left: scrollTo,
        behavior,
      })
    }, 10)
  }

  const handleScroll = useCallback(
    debounce((_e: SyntheticEvent) => {
      if (ref.current) {
        setRowScrollPosition(() => ref.current?.scrollLeft ?? 0)
      }
    }, DELAY_SET_SCROLL_POSITION),
    []
  )

  const next = (): void => {
    scrollToItem({ forward: true })
  }

  const prev = (): void => {
    scrollToItem({ forward: false })
  }

  return {
    next,
    prev,
    handleScroll,
    scrollToItem,
    focusOnItem,
  }
}
